
import { 
  Card, 
  CardContent,
  CardFooter
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Handshake, DollarSign, ArrowRight, Check } from "lucide-react";

interface PartnershipRecommendationProps {
  partnership: {
    id: number;
    supplier: string;
    category: string;
    currentSpend: number;
    requiredForTier: number;
    potentialSavings: number;
    savingsPercent: number;
    additionalBenefits: string[];
  };
}

export const PartnershipRecommendation = ({ partnership }: PartnershipRecommendationProps) => {
  const progressPercent = (partnership.currentSpend / partnership.requiredForTier) * 100;
  const remaining = partnership.requiredForTier - partnership.currentSpend;
  
  return (
    <Card className="overflow-hidden">
      <div className="h-2 bg-blue-500" />
      <CardContent className="p-4">
        <div className="space-y-4">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-semibold text-lg">{partnership.supplier}</h3>
              <p className="text-muted-foreground text-sm">{partnership.category}</p>
            </div>
            <Handshake className="h-5 w-5 text-blue-500" />
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progress to next tier</span>
              <span className="font-medium">{progressPercent.toFixed(0)}%</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
            
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>${partnership.currentSpend.toFixed(0)}</span>
              <span>${partnership.requiredForTier.toFixed(0)}</span>
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex justify-between">
              <span className="text-sm">Additional spend needed</span>
              <span className="font-semibold">${remaining.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">Potential savings</span>
              <Badge variant="default" className="flex items-center bg-green-600">
                <DollarSign className="h-3 w-3 mr-1" />
                ${partnership.potentialSavings.toFixed(2)}
              </Badge>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="bg-muted/30 p-3 flex flex-col gap-2">
        <div className="w-full flex flex-wrap gap-1">
          {partnership.additionalBenefits.map((benefit, i) => (
            <Badge key={i} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              <Check className="h-3 w-3 mr-1" /> {benefit}
            </Badge>
          ))}
        </div>
        <Button variant="outline" size="sm" className="w-full mt-2">
          <Handshake className="h-4 w-4 mr-1" />
          Explore Partnership
        </Button>
      </CardFooter>
    </Card>
  );
};
